import * as React from 'react';
import { Rifm } from 'rifm';
import { formatCprNumber } from '../helper/format';
import { callAll } from '../helper/fp';
import { isString } from '../helper/typeguard';
import { validateCpr, validateCprAge } from '../helper/validate';
import { Input, InputProps } from '../Input/Input';

export type CprInvalidReasons = 'INVALID_FORMAT' | 'TOO_YOUNG' | 'TOO_OLD';

export type CprInputProps = {
  onValidityChange?: (isValid: boolean | undefined) => void;
  onErrorReasonChange?: (reason: CprInvalidReasons | null) => void;
  /** control if `onValidityChange` and `onErrorReasonChange` should be called onChange, else they will be called onBlur */
  validateOnChange?: boolean;
  minAge?: number;
  maxAge?: number;
} & InputProps;

class CprInputComponent extends React.Component<
  CprInputProps & { innerRef: React.Ref<HTMLInputElement> },
  { value: string }
> {
  state = {
    value: '',
  };

  isControl = (propKey: keyof CprInputProps) => this.props[propKey] !== undefined;

  getValue = () => (this.isControl('value') ? this.props.value : this.state.value);

  sanitizeValue = (value: string) => value && value.replace(/\s|-/g, '');

  handleChangeValue = (value: string) => {
    const sanitizedValue = this.sanitizeValue(value);
    const {
      onValidityChange,
      validateOnChange,
      onErrorReasonChange,
      onChangeValue,
      minAge,
      maxAge,
      required,
    } = this.props;

    if (validateOnChange) {
      const cprFormatIsValid = validateCpr(sanitizedValue);
      const ageOutOfRangeError = validateCprAge(sanitizedValue, {
        maxAge,
        minAge,
      });

      if (onValidityChange) {
        if (value === '' && !required) {
          onValidityChange(undefined);
        } else {
          onValidityChange(cprFormatIsValid && ageOutOfRangeError === null);
        }
      }
      if (onErrorReasonChange) {
        onErrorReasonChange(cprFormatIsValid ? ageOutOfRangeError : 'INVALID_FORMAT');
      }
    }
    if (onChangeValue) {
      onChangeValue(sanitizedValue);
    }
    if (!this.isControl('value')) {
      this.setState({
        value: sanitizedValue,
      });
    }
  };

  handleBlur = () => {
    const props = this.props;
    if (!this.props.validateOnChange) {
      const value = this.sanitizeValue(this.getValue() as string);
      const cprFormatIsValid = validateCpr(value);
      const ageOutOfRangeError = validateCprAge(value, {
        maxAge: props.maxAge,
        minAge: props.minAge,
      });

      if (props.onValidityChange) {
        if (value === '' && !props.required) {
          props.onValidityChange(undefined);
        } else {
          props.onValidityChange(cprFormatIsValid && ageOutOfRangeError === null);
        }
      }
      if (props.onErrorReasonChange) {
        props.onErrorReasonChange(cprFormatIsValid ? ageOutOfRangeError : 'INVALID_FORMAT');
      }
    }
  };

  render() {
    const {
      onValidityChange,
      validateOnChange,
      onErrorReasonChange,
      innerRef,
      onChangeValue,
      onBlur,
      minAge,
      maxAge,
      value,
      onChange,
      maxLength = 11,
      ...inputProps
    } = this.props;

    const val = this.getValue() || '';

    return (
      <Rifm
        value={isString(val) ? val : val.toString()}
        onChange={this.handleChangeValue}
        format={formatCprNumber}
        replace={replaceCprValue}
      >
        {({ value: maskedValue, onChange: onInputChange }) => (
          <Input
            onChange={callAll(onInputChange, onChange)}
            onBlur={callAll(onBlur, this.handleBlur)}
            value={maskedValue}
            maxLength={maxLength}
            {...inputProps}
            ref={innerRef}
          />
        )}
      </Rifm>
    );
  }
}

function replaceCprValue(val: string) {
  return val && val.replace(/[^0-9-]/g, '');
}

/**
 * CprInput is a wrapper around Input component with formatting and validity check
 */
export const CprInput = React.forwardRef<HTMLInputElement, CprInputProps>((props, ref) => (
  <CprInputComponent {...(props as Exclude<keyof CprInputProps, 'ref'>)} innerRef={ref} />
));

export default CprInput;
